import axios from 'axios';
import { adminApiUrl } from '../config/api';
import { adminAuthService } from './adminAuth';

export interface IncidentReport {
  reportId: string;
  userId: string;
  incidentType: 'harassment' | 'theft' | 'violence' | 'fraud' | 'unsafe_behavior' | 'inappropriate_content' | 'other';
  description: string;
  bookingId?: string;
  reportedUserId?: string;
  status: 'pending' | 'under_review' | 'resolved' | 'dismissed';
  assignedTo?: string;
  adminNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IncidentReportListResponse {
  reports: IncidentReport[];
  count: number;
  lastEvaluatedKey?: string;
}

export interface ListIncidentReportsParams {
  limit?: number;
  lastEvaluatedKey?: string;
  search?: string;
  incidentType?: string;
  status?: string;
  userId?: string;
  reportedUserId?: string;
  bookingId?: string;
  startDate?: string;
  endDate?: string;
}

export interface UpdateIncidentReportRequest {
  status?: 'pending' | 'under_review' | 'resolved' | 'dismissed';
  assignedTo?: string;
  adminNotes?: string;
}

class IncidentReportsManagementService {
  private api = axios.create({
    baseURL: adminApiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  private async getAuthHeaders() {
    const token = adminAuthService.getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  async listIncidentReports(params?: ListIncidentReportsParams): Promise<IncidentReportListResponse> {
    const headers = await this.getAuthHeaders();
    const queryParams = new URLSearchParams();
    
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.incidentType) queryParams.append('incidentType', params.incidentType);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.reportedUserId) queryParams.append('reportedUserId', params.reportedUserId);
    if (params?.bookingId) queryParams.append('bookingId', params.bookingId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await this.api.get<{data: IncidentReportListResponse}>(
      `/admin/incident-reports?${queryParams.toString()}`,
      { headers }
    );
    return response.data.data;
  }

  async getIncidentReport(reportId: string): Promise<IncidentReport> {
    const headers = await this.getAuthHeaders();
    const response = await this.api.get<{data: {report: IncidentReport}}>(
      `/admin/incident-reports/${reportId}`,
      { headers }
    );
    return response.data.data.report;
  }

  async updateIncidentReport(reportId: string, updates: UpdateIncidentReportRequest): Promise<IncidentReport> {
    const headers = await this.getAuthHeaders();
    const response = await this.api.put<{data: {report: IncidentReport}}>(
      `/admin/incident-reports/${reportId}`,
      updates,
      { headers }
    );
    return response.data.data.report;
  }
}

export const incidentReportsManagementService = new IncidentReportsManagementService();
